import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";

import { ContestFormHeader } from "./ContestFormHeader";

import { useTrans } from "../../../hooks";
import { Loader } from "../../../shared-components";

import LanguageField from "./WattysFormFields/LanguageField";
import SeriesIdentificationField from "./WattysFormFields/SeriesIdentification/SeriesIdentificationField";
import AIDisclaimerField from "./WattysFormFields/AIDisclaimerField";
import MatureThemesField from "./WattysFormFields/MatureThemesField";
import LoglineField from "./WattysFormFields/LoglineField";
import PlotSummaryField from "./WattysFormFields/PlotSummaryField";
import EmailField from "./WattysFormFields/EmailField";
import CharacterRepresentationField from "./WattysFormFields/CharacterRepresentationField";
import TargetAudienceField from "./WattysFormFields/TargetAudienceField";
import PrimaryGenreIdentificationField from "./WattysFormFields/PrimaryGenreIdentificationField";
import FinalConsentField from "./WattysFormFields/FinalConsentField";

import { getLength, formToSubmissionBody } from "./helpers";
import {
  EMAIL_CHARACTER_MAX,
  LOGLINE_CHARACTER_MAX,
  LOGLINE_CHARACTER_MIN,
  PLOT_SUMMARY_CHARACTER_MAX,
  PLOT_SUMMARY_CHARACTER_MIN,
  OTHER_MATURE_THEMES_CHARACTER_MAX,
  OTHER_RESUBMISSION_CHARACTER_MAX,
  WATTYS_CLOSING_DATETIME,
  VALID_EMAIL_REGEX,
  CURRENT_WATTYS_YEAR,
  MAP_WATTYS_ELIGIBILITY_TO_LINKS,
  DEFAULT_ELIGIBILITY_LINK,
  DEFAULT_IDENTITIES_LINK,
  MAP_IDENTITIES_LINK,
  RULES_AND_REGULATIONS_LINK
} from "./constants";
import LegalConsentField from "./WattysFormFields/LegalConsentField";
import ResubmissionField from "./WattysFormFields/ResubmissionField";

let submitted = false;

export default function WattysForm({
  submitForm,
  closeModal,
  isStandalonePage,
  storyId,
  setContestForm,
  formState,
  formIsOpen
}) {
  // Default values from server
  const { data: initialData } = formState;

  let { hasSubmitted, isLoading } = formState;
  let disableForm = hasSubmitted || isLoading || !formIsOpen;

  // States
  const [form, setForm] = useState(initialData);
  const [emailConfirmField, setEmailConfirmField] = useState("");
  const [eligibilityCriteriaLink, setEligibilityCriteriaLink] = useState("");
  const [identitiesLink, setIdentitiesLink] = useState("");
  const [isFrenchForm, setIsFrenchForm] = useState(false);
  const [fieldStatus, setStatus] = useState({
    fieldName: "",
    fieldInvalid: false
  });
  const [requiredAIDisclaimerError, setRequiredAIDisclaimerError] = useState(
    false
  );
  const [requiredMatureThemesError, setRequiredMatureThemesError] = useState(
    false
  );

  // Update form state when redux store finishes fetching
  useEffect(
    () => {
      setForm(initialData);
      const displayLanguage = parseInt(app.get("language"), 10);
      const eligibilityCriteriaLink =
        MAP_WATTYS_ELIGIBILITY_TO_LINKS[displayLanguage] ||
        DEFAULT_ELIGIBILITY_LINK;
      setEligibilityCriteriaLink(eligibilityCriteriaLink);
      const identitiesLink =
        MAP_IDENTITIES_LINK[displayLanguage] || DEFAULT_IDENTITIES_LINK;
      setIdentitiesLink(identitiesLink);
      if (displayLanguage === 2) {
        setIsFrenchForm(true);
      }
    },
    [initialData]
  );

  const { trans } = useTrans();

  const isFieldInvalid = form => {
    return (
      form.storyMaturity === "" ||
      (form.storyMaturity === "MATURE" &&
        (form.matureThemes.length === 0 ||
          (form.matureThemes.includes("OTHER") &&
            form.matureThemeOther === ""))) ||
      form.plotSummary === "" ||
      form.logLine === "" ||
      form.writerEmail === "" ||
      form.writerEmail.match(VALID_EMAIL_REGEX) === null ||
      emailConfirmField === "" ||
      emailConfirmField.match(VALID_EMAIL_REGEX) === null ||
      emailConfirmField !== form.writerEmail ||
      !form.legalConsent ||
      !form.finalConsent ||
      form.genrePrimary === "" ||
      (form.diversity.includes("OTHER") && form.diversityOther === "") ||
      (form.isSeries === "" || // field is empty
        (form.isSeries && // story is part of a series but the subsequent field is empty
          form.isSeriesStandalone === "")) ||
      form.aiDisclaimer.length === 0 ||
      (form.resubmission === "OTHER" && form.resubmissionOther === "") ||
      form.targetAudience === ""
    );
  };

  function onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    // Build data to send over.
    submitted = true;

    if (isFieldInvalid(form)) {
      setStatus({
        fieldInvalid: true
      });
    }

    const body = formToSubmissionBody(parseInt(storyId), form);

    const seriesIdentificationIsValid =
      form.isSeries !== "" && // field is not empty
      (!form.isSeries || // story is not part of a series
        (form.isSeries && // story is part of a series and subsequent field is valid
          form.isSeriesStandalone !== ""));

    const canSubmit =
      !disableForm &&
      (form.logLine &&
        getLength(form.logLine) <= LOGLINE_CHARACTER_MAX &&
        getLength(form.logLine) >= LOGLINE_CHARACTER_MIN &&
        form.storyMaturity &&
        (form.storyMaturity === "MATURE"
          ? form.matureThemes.length !== 0
          : true) &&
        form.plotSummary &&
        getLength(form.plotSummary) >= PLOT_SUMMARY_CHARACTER_MIN &&
        getLength(form.plotSummary) <= PLOT_SUMMARY_CHARACTER_MAX) &&
      form.writerEmail &&
      getLength(form.writerEmail) <= EMAIL_CHARACTER_MAX &&
      form.writerEmail.match(VALID_EMAIL_REGEX) !== null &&
      emailConfirmField &&
      getLength(emailConfirmField) <= EMAIL_CHARACTER_MAX &&
      emailConfirmField.match(VALID_EMAIL_REGEX) !== null &&
      form.writerEmail === emailConfirmField &&
      !requiredMatureThemesError &&
      (form.matureThemes.includes("OTHER")
        ? form.matureThemeOther !== ""
        : true) &&
      getLength(form.matureThemeOther) <= OTHER_MATURE_THEMES_CHARACTER_MAX &&
      form.finalConsent &&
      form.legalConsent &&
      form.genrePrimary &&
      form.aiDisclaimer.length !== 0 &&
      !requiredAIDisclaimerError &&
      form.storyStatus === "COMPLETE" &&
      (form.resubmission === "OTHER" ? form.resubmissionOther : true) &&
      getLength(form.resubmissionOther) <= OTHER_RESUBMISSION_CHARACTER_MAX &&
      seriesIdentificationIsValid &&
      form.targetAudience;

    if (canSubmit) {
      setContestForm(storyId, body).then(hasAddedWattysTag => {
        submitForm(hasAddedWattysTag);
      });
    }
  }

  const onMatureThemesChange = useCallback(
    event => {
      let newMatureThemes;
      const { name, checked } = event.target;
      setStatus({ fieldName: "matureThemes", fieldInvalid: false });
      if (checked) {
        // Add mature theme
        newMatureThemes = [...form.matureThemes, name];
      } else {
        // Remove mature theme
        newMatureThemes = form.matureThemes.filter(
          matureTheme => matureTheme !== name
        );
      }
      setRequiredMatureThemesError(newMatureThemes.length === 0);

      if (name === "OTHER") {
        setForm({
          ...form,
          matureThemes: newMatureThemes,
          matureThemeOther: ""
        });
      } else {
        setForm({
          ...form,
          matureThemes: newMatureThemes
        });
      }
    },
    [form]
  );

  function onOtherMatureFieldTextChange(event) {
    const { name, value } = event.target;
    setStatus({ fieldName: name, fieldInvalid: false });

    setForm({
      ...form,
      matureThemeOther: value
    });
  }

  const onRadioBtnChange = event => {
    const { name, value } = event.target;

    let boolVal = value !== 0;
    if (name == "finalConsent" || name == "legalConsent") {
      boolVal = event.target.checked;
    }

    setForm({ ...form, [name]: boolVal });
  };

  function onFormChange(event) {
    const { name, value } = event.target;
    if (name === "genrePrimary") {
      setForm({
        ...form,
        [name]: value
      });
    } else if (name === "storyMaturity") {
      setStatus({ fieldName: name, fieldInvalid: false });
      if (value === "FOR_EVERYONE") {
        setForm({
          ...form,
          [name]: value,
          matureThemes: [],
          matureThemeOther: ""
        });
        setRequiredMatureThemesError(false);
      } else {
        setForm({
          ...form,
          [name]: value
        });
      }
    } else {
      setStatus({ fieldName: name, fieldInvalid: false });
      setForm({
        ...form,
        [name]: value
      });
    }
  }

  const onSeriesIdentificationChange = event => {
    const { name, value } = event.target;
    setStatus({ fieldName: name, fieldInvalid: false });

    setForm({
      ...form,
      [name]: value === "yes"
    });
  };

  const onAIDisclaimerChange = useCallback(
    event => {
      let newAIDisclaimer;
      const { name, checked } = event.target;
      setStatus({ fieldName: "aiDisclaimer", fieldInvalid: false });
      if (checked) {
        // Add option
        if (form.aiDisclaimer.includes("AUTHENTIC")) {
          newAIDisclaimer = form.aiDisclaimer.filter(
            option => option !== "AUTHENTIC"
          );
          newAIDisclaimer = [...newAIDisclaimer, name];
        } else if (name === "AUTHENTIC") {
          newAIDisclaimer = ["AUTHENTIC"];
        } else {
          newAIDisclaimer = [...form.aiDisclaimer, name];
        }
      } else {
        // Remove option
        newAIDisclaimer = form.aiDisclaimer.filter(option => option !== name);
      }
      setRequiredAIDisclaimerError(newAIDisclaimer.length === 0);
      setForm({
        ...form,
        aiDisclaimer: newAIDisclaimer
      });
    },
    [form]
  );

  const onCharacterRepresentationChange = event => {
    let newCharacterRepresentation;
    const { name, checked } = event.target;
    setStatus({ fieldName: name, fieldInvalid: false });
    if (checked) {
      if (form.diversity.includes("NONE")) {
        newCharacterRepresentation = form.diversity.filter(
          identity => identity !== "NONE"
        );
        newCharacterRepresentation = [...newCharacterRepresentation, name];
      } else if (name === "NONE") {
        newCharacterRepresentation = ["NONE"];
      } else {
        newCharacterRepresentation = [...form.diversity, name];
      }
    } else {
      // Remove identity
      newCharacterRepresentation = form.diversity.filter(
        identity => identity !== name
      );
    }
    if (name === "OTHER" || (name === "NONE" && checked === true)) {
      setForm({
        ...form,
        diversity: newCharacterRepresentation,
        diversityOther: ""
      });
    } else {
      setForm({
        ...form,
        diversity: newCharacterRepresentation
      });
    }
  };

  if (isLoading && hasSubmitted) {
    return (
      <div className="loading-spinner">
        <Loader />
      </div>
    );
  }
  return (
    <div id="contest-form">
      <form>
        <div
          className={classNames("overflow-container", {
            disabled: isLoading
          })}
        >
          <ContestFormHeader
            subtitle={
              /* prettier-ignore */ trans("You’ve done the work, now it’s time to show it off! Complete the form below to officially enter your story into the %s Watty Awards.", CURRENT_WATTYS_YEAR)
            }
            closeModal={closeModal}
            isStandalonePage={isStandalonePage}
          />
          <div className="form-deadline">
            <div
              className="deadline-text"
              dangerouslySetInnerHTML={{
                /* prettier-ignore */ __html: trans("Submissions close July 23rd, 2026 at 11:59:59 PM EDT. For full eligibility criteria, refer to the <a class='watty-regulation-link' href=%s target=\"_blank\" rel=\"noopener noreferrer\">Rules and Regulations</a>.", RULES_AND_REGULATIONS_LINK)
              }}
            />
          </div>
          <div className="form-content">
            {!formIsOpen && (
              <div
                className="form-message-box"
                dangerouslySetInnerHTML={{
                  __html: /* prettier-ignore */ trans("The %s Watty Awards are now closed. Thanks for your participation!", CURRENT_WATTYS_YEAR)
                }}
              />
            )}
            {hasSubmitted && (
              <div
                className="form-message-box"
                dangerouslySetInnerHTML={{
                  __html: /* prettier-ignore */ trans("You are viewing your submission in The %s Watty Awards. Changes cannot be made.", CURRENT_WATTYS_YEAR)
                }}
              />
            )}
            {!hasSubmitted &&
              formIsOpen && (
                <>
                  <div
                    className="eligbility-requirements-title"
                    id="eligbility-requirements-title"
                    dangerouslySetInnerHTML={{
                      /* prettier-ignore */ __html: trans("Please confirm that you meet the <a href=%s target=\"_blank\" rel=\"noopener noreferrer\">eligibility requirements</a>. Stories that do not meet the eligibility requirements will be disqualified.", eligibilityCriteriaLink)
                    }}
                  />
                  <div
                    className="form-title"
                    id="form-title"
                    dangerouslySetInnerHTML={{
                      /* prettier-ignore */ __html: trans("We need a few more details about the story you are entering:<br><span class='bold'>“%s”</span>", formState.title)
                    }}
                  />
                </>
              )}

            <LanguageField form={form} storyId={storyId} />

            <SeriesIdentificationField
              form={form}
              disableForm={disableForm}
              onSeriesIdentificationChange={onSeriesIdentificationChange}
              fieldStatus={fieldStatus}
              submitted={submitted}
            />

            <AIDisclaimerField
              form={form}
              disableForm={disableForm}
              onAIDisclaimerChange={onAIDisclaimerChange}
              requiredAIDisclaimerError={requiredAIDisclaimerError}
              fieldStatus={fieldStatus}
              submitted={submitted}
            />

            <MatureThemesField
              form={form}
              disableForm={disableForm}
              isFrenchForm={isFrenchForm}
              onMatureThemesChange={onMatureThemesChange}
              requiredMatureThemesError={requiredMatureThemesError}
              fieldStatus={fieldStatus}
              submitted={submitted}
              onOtherMatureFieldTextChange={onOtherMatureFieldTextChange}
              onFormChange={onFormChange}
            />

            <PrimaryGenreIdentificationField
              form={form}
              onFormChange={onFormChange}
              fieldStatus={fieldStatus}
              submitted={submitted}
              storyLanguage={form.language}
            />

            <TargetAudienceField
              form={form}
              disableForm={disableForm}
              onFormChange={onFormChange}
              fieldStatus={fieldStatus}
              submitted={submitted}
            />

            <CharacterRepresentationField
              form={form}
              disableForm={disableForm}
              fieldStatus={fieldStatus}
              submitted={submitted}
              onCharacterRepresentationChange={onCharacterRepresentationChange}
              onFormChange={onFormChange}
              identitiesLink={identitiesLink}
            />

            <LoglineField
              form={form}
              disableForm={disableForm}
              onFormChange={onFormChange}
              fieldStatus={fieldStatus}
              submitted={submitted}
            />

            <PlotSummaryField
              form={form}
              disableForm={disableForm}
              onFormChange={onFormChange}
              fieldStatus={fieldStatus}
              submitted={submitted}
            />

            <ResubmissionField
              form={form}
              disableForm={disableForm}
              onFormChange={onFormChange}
            />

            <EmailField
              form={form}
              disableForm={disableForm}
              onPreferredEmailChange={onFormChange}
              fieldStatus={fieldStatus}
              submitted={submitted}
              hasSubmitted={hasSubmitted}
              emailConfirmField={emailConfirmField}
              setEmailConfirmField={setEmailConfirmField}
            />

            <LegalConsentField
              form={form}
              disableForm={disableForm}
              onRadioBtnChange={onRadioBtnChange}
              fieldStatus={fieldStatus}
              submitted={submitted}
            />
          </div>
          <div className="footer-section-submission">
            <div className="left-padding" />
            <FinalConsentField
              form={form}
              disableForm={disableForm}
              onRadioBtnChange={onRadioBtnChange}
              fieldStatus={fieldStatus}
              submitted={submitted}
              isStandalonePage={isStandalonePage}
              hasSubmitted={hasSubmitted}
              formIsOpen={formIsOpen}
              onSubmit={onSubmit}
            />
            <div className="left-padding" />
          </div>
        </div>
      </form>
    </div>
  );
}

WattysForm.propTypes = {
  submitForm: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  isStandalonePage: PropTypes.bool.isRequired,
  fetchContestForm: PropTypes.func,
  setContestForm: PropTypes.func,
  storyId: PropTypes.string,
  formIsOpen: PropTypes.bool,
  formState: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
    hasSubmitted: PropTypes.bool.isRequired,
    cover: PropTypes.string.isRequired,
    user: PropTypes.string.isRequired,
    title: PropTypes.string,
    data: PropTypes.shape({
      storyId: PropTypes.number,
      contest: PropTypes.string,
      language: PropTypes.string.isRequired,
      storyStatus: PropTypes.string,
      isSeries: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      isSeriesStandalone: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.string
      ]),
      aiDisclaimer: PropTypes.arrayOf(PropTypes.string).isRequired,
      diversity: PropTypes.arrayOf(PropTypes.string).isRequired,
      diversityOther: PropTypes.string,
      targetAudience: PropTypes.string.isRequired,
      genrePrimary: PropTypes.string.isRequired,
      matureThemes: PropTypes.arrayOf(PropTypes.string).isRequired,
      matureThemeOther: PropTypes.string,
      storyMaturity: PropTypes.string,
      logLine: PropTypes.string.isRequired,
      plotSummary: PropTypes.string.isRequired,
      resubmission: PropTypes.string,
      resubmissionOther: PropTypes.string,
      legalConsent: PropTypes.bool.isRequired,
      writerEmail: PropTypes.string,
      finalConsent: PropTypes.bool
    })
  })
};

WattysForm.defaultProps = {
  formIsOpen: new Date().getTime() < WATTYS_CLOSING_DATETIME
};
